import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

/** The one-time messages a guard's redirect hands the screen it lands on; each has a toast there. */
const SESSION_FLASHES = ['identity-verified', 'account-created'] as const;

export type SessionFlash = (typeof SESSION_FLASHES)[number];

/** The router state a redirect carries: `<Navigate state={flashState('account-created')} />`. */
export const flashState = (flash: SessionFlash) => ({ flash });

// Router state outlives the bundle (the browser keeps history state across reloads), so it is parsed like any
// other outside data rather than trusted.
const flashStateSchema = z.object({ flash: z.enum(SESSION_FLASHES) });

/**
 * Runs `onFlash` once when the screen was reached with `flash` in its router state, then clears that state, so a
 * reload or a Back to this page doesn't show the message again. A flash the screen doesn't handle is left alone.
 */
export const useSessionFlash = (flash: SessionFlash, onFlash: () => void) => {
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = flashStateSchema.safeParse(location.state);
  const flashed = parsed.success && parsed.data.flash === flash;
  // Once per arrival: StrictMode runs effects twice in dev, and the screen re-renders before the state clears.
  const consumedKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!flashed || consumedKey.current === location.key) {
      return;
    }
    consumedKey.current = location.key;
    onFlash();
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null },
    );
  }, [flashed, location, navigate, onFlash]);
};
